"use client";

import { useState, useRef, useEffect, useCallback } from "react";

type TooltipPosition = "top" | "bottom" | "left" | "right" | "top-left" | "top-right" | "bottom-left" | "bottom-right";

interface TooltipProps {
  text?: string;
  content?: React.ReactNode; // Rich content alternative to text (multi-line, styled)
  children: React.ReactNode;
  position?: TooltipPosition;
  icon?: React.ReactNode; // Optional icon to show above the text
  maxWidth?: number; // Max width for rich content tooltips
}

// Padding from viewport edges
const EDGE_PADDING = 8;

export function Tooltip({ text, content, children, position = "top", icon, maxWidth }: TooltipProps) {
  const [show, setShow] = useState(false);
  const [adjustedPosition, setAdjustedPosition] = useState<TooltipPosition>(position);
  const [nudgeX, setNudgeX] = useState(0); // Horizontal nudge to stay in viewport
  const triggerRef = useRef<HTMLSpanElement>(null);
  const tooltipRef = useRef<HTMLSpanElement>(null);
  // Track theme for reactive updates
  const [isDark, setIsDark] = useState(true);

  useEffect(() => {
    const checkTheme = () => {
      setIsDark(document.documentElement.getAttribute('data-theme') === 'dark');
    };
    checkTheme();

    // Watch for theme changes
    const observer = new MutationObserver(checkTheme);
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] });
    return () => observer.disconnect();
  }, []);

  // Theme-aware colors
  const bgColor = isDark ? "rgba(40, 40, 40, 0.95)" : "rgba(255, 255, 255, 0.95)";
  const textColor = isDark ? "white" : "rgba(0, 0, 0, 0.85)";
  const borderColor = isDark ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.1)";

  // Calculate if tooltip needs repositioning to stay in viewport
  const calculatePosition = useCallback(() => {
    if (!triggerRef.current || !tooltipRef.current) return;

    const triggerRect = triggerRef.current.getBoundingClientRect();
    const tooltipRect = tooltipRef.current.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    let newPosition = position;
    let xNudge = 0;

    // Check vertical overflow and flip if needed
    if (position === "top" || position === "top-left" || position === "top-right") {
      // Would overflow top?
      if (triggerRect.top - tooltipRect.height - 8 < EDGE_PADDING) {
        // Flip to bottom variant
        if (position === "top") newPosition = "bottom";
        else if (position === "top-left") newPosition = "bottom-left";
        else if (position === "top-right") newPosition = "bottom-right";
      }
    } else if (position === "bottom" || position === "bottom-left" || position === "bottom-right") {
      // Would overflow bottom?
      if (triggerRect.bottom + tooltipRect.height + 8 > viewportHeight - EDGE_PADDING) {
        // Flip to top variant
        if (position === "bottom") newPosition = "top";
        else if (position === "bottom-left") newPosition = "top-left";
        else if (position === "bottom-right") newPosition = "top-right";
      }
    } else if (position === "left") {
      // Would overflow left?
      if (triggerRect.left - tooltipRect.width - 8 < EDGE_PADDING) {
        newPosition = "right";
      }
    } else if (position === "right") {
      // Would overflow right?
      if (triggerRect.right + tooltipRect.width + 8 > viewportWidth - EDGE_PADDING) {
        newPosition = "left";
      }
    }

    // Check horizontal overflow for all top/bottom variants and nudge
    if (newPosition.startsWith("top") || newPosition.startsWith("bottom")) {
      // Calculate where the tooltip actually sits horizontally
      let tooltipLeft: number;
      let tooltipRight: number;

      if (newPosition === "top" || newPosition === "bottom") {
        // Centered: tooltip centers on trigger
        const center = triggerRect.left + triggerRect.width / 2;
        tooltipLeft = center - tooltipRect.width / 2;
        tooltipRight = center + tooltipRect.width / 2;
      } else if (newPosition === "top-right" || newPosition === "bottom-right") {
        // Anchored left edge to trigger left
        tooltipLeft = triggerRect.left;
        tooltipRight = triggerRect.left + tooltipRect.width;
      } else {
        // top-left / bottom-left: anchored right edge to trigger right
        tooltipLeft = triggerRect.right - tooltipRect.width;
        tooltipRight = triggerRect.right;
      }

      if (tooltipLeft < EDGE_PADDING) {
        xNudge = EDGE_PADDING - tooltipLeft;
      } else if (tooltipRight > viewportWidth - EDGE_PADDING) {
        xNudge = viewportWidth - EDGE_PADDING - tooltipRight;
      }
    }

    setAdjustedPosition(newPosition);
    setNudgeX(xNudge);
  }, [position]);

  // Recalculate when shown
  useEffect(() => {
    if (show) {
      // Small delay to ensure tooltip is rendered
      requestAnimationFrame(calculatePosition);
    } else {
      // Reset when hidden
      setAdjustedPosition(position);
      setNudgeX(0);
    }
  }, [show, calculatePosition, position]);

  // Use adjusted position for all styling
  const pos = adjustedPosition;

  const getPositionClasses = () => {
    switch (pos) {
      case "left":
        return "absolute right-full top-1/2 mr-2 pointer-events-none z-[300]";
      case "right":
        return "absolute left-full top-1/2 ml-2 pointer-events-none z-[300]";
      case "bottom":
        return "absolute top-full left-1/2 mt-2 pointer-events-none z-[300]";
      case "top-left":
        return "absolute bottom-full right-0 mb-2 pointer-events-none z-[300]";
      case "top-right":
        return "absolute bottom-full left-0 mb-2 pointer-events-none z-[300]";
      case "bottom-left":
        return "absolute top-full right-0 mt-2 pointer-events-none z-[300]";
      case "bottom-right":
        return "absolute top-full left-0 mt-2 pointer-events-none z-[300]";
      default: // top
        return "absolute bottom-full left-1/2 mb-2 pointer-events-none z-[300]";
    }
  };

  const getTransform = () => {
    // Include nudgeX for horizontal adjustment
    const nudge = nudgeX !== 0 ? ` translateX(${nudgeX}px)` : "";

    if (pos === "left") {
      return show ? `translateY(-50%) translateX(0)${nudge}` : `translateY(-50%) translateX(4px)${nudge}`;
    }
    if (pos === "right") {
      return show ? `translateY(-50%) translateX(0)${nudge}` : `translateY(-50%) translateX(-4px)${nudge}`;
    }
    if (pos === "bottom") {
      return show ? `translateX(calc(-50% + ${nudgeX}px)) translateY(0)` : `translateX(calc(-50% + ${nudgeX}px)) translateY(-4px)`;
    }
    if (pos === "top-left" || pos === "top-right") {
      return show ? `translateY(0)${nudge}` : `translateY(4px)${nudge}`;
    }
    if (pos === "bottom-left" || pos === "bottom-right") {
      return show ? `translateY(0)${nudge}` : `translateY(-4px)${nudge}`;
    }
    // top (default)
    return show ? `translateX(calc(-50% + ${nudgeX}px)) translateY(0)` : `translateX(calc(-50% + ${nudgeX}px)) translateY(4px)`;
  };

  // Caret position - adjust for nudge on top/bottom centered tooltips
  const getCaretClasses = () => {
    switch (pos) {
      case "left":
        return "absolute top-1/2 -right-[6px]";
      case "right":
        return "absolute top-1/2 -left-[6px]";
      case "bottom":
        return "absolute -top-[6px]";
      case "top-left":
        return "absolute right-3 -bottom-[6px]";
      case "top-right":
        return "absolute left-3 -bottom-[6px]";
      case "bottom-left":
        return "absolute right-3 -top-[6px]";
      case "bottom-right":
        return "absolute left-3 -top-[6px]";
      default: // top
        return "absolute -bottom-[6px]";
    }
  };

  const getCaretStyle = (): React.CSSProperties => {
    // For top/bottom, position caret relative to where trigger is (account for nudge)
    const caretOffset = pos === "top" || pos === "bottom" ? `calc(50% - ${nudgeX}px)` : undefined;

    if (pos === "left") {
      return {
        transform: "translateY(-50%)",
        width: 0,
        height: 0,
        borderTop: "6px solid transparent",
        borderBottom: "6px solid transparent",
        borderLeft: `6px solid ${bgColor}`,
        filter: "drop-shadow(2px 0 2px rgba(0, 0, 0, 0.2))",
      };
    }
    if (pos === "right") {
      return {
        transform: "translateY(-50%)",
        width: 0,
        height: 0,
        borderTop: "6px solid transparent",
        borderBottom: "6px solid transparent",
        borderRight: `6px solid ${bgColor}`,
        filter: "drop-shadow(-2px 0 2px rgba(0, 0, 0, 0.2))",
      };
    }
    if (pos === "bottom" || pos === "bottom-left" || pos === "bottom-right") {
      return {
        left: caretOffset,
        transform: pos === "bottom" ? "translateX(-50%)" : undefined,
        width: 0,
        height: 0,
        borderLeft: "6px solid transparent",
        borderRight: "6px solid transparent",
        borderBottom: `6px solid ${bgColor}`,
        filter: "drop-shadow(0 -2px 2px rgba(0, 0, 0, 0.2))",
      };
    }
    // top, top-left, top-right
    return {
      left: caretOffset,
      transform: pos === "top" ? "translateX(-50%)" : undefined,
      width: 0,
      height: 0,
      borderLeft: "6px solid transparent",
      borderRight: "6px solid transparent",
      borderTop: `6px solid ${bgColor}`,
      filter: "drop-shadow(0 2px 2px rgba(0, 0, 0, 0.2))",
    };
  };

  // Track touch vs mouse to handle both correctly
  const isTouchRef = useRef(false);

  const handleTouchStart = useCallback(() => {
    isTouchRef.current = true;
  }, []);

  const handleMouseEnter = useCallback(() => {
    if (!isTouchRef.current) setShow(true);
  }, []);

  const handleMouseLeave = useCallback(() => {
    if (!isTouchRef.current) setShow(false);
  }, []);

  const handleClick = useCallback(() => {
    if (isTouchRef.current) {
      setShow(prev => !prev);
      isTouchRef.current = false;
    }
  }, []);

  // Tap outside to dismiss on mobile
  useEffect(() => {
    if (!show) return;
    const handleOutside = (e: Event) => {
      if (triggerRef.current && !triggerRef.current.contains(e.target as Node)) {
        setShow(false);
      }
    };
    document.addEventListener("pointerdown", handleOutside);
    return () => document.removeEventListener("pointerdown", handleOutside);
  }, [show]);

  return (
    <span
      ref={triggerRef}
      className="relative inline-block"
      onTouchStart={handleTouchStart}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onClick={handleClick}
    >
      {children}
      <span
        ref={tooltipRef}
        className={getPositionClasses()}
        style={{
          opacity: show ? 1 : 0,
          transform: getTransform(),
          transition: "opacity 150ms ease, transform 150ms ease",
          display: "block",
        }}
      >
        <span
          className={`relative px-3 py-1.5 rounded-lg text-xs font-medium ${content ? "whitespace-normal" : "whitespace-nowrap"}`}
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: content ? "flex-start" : "center",
            gap: icon ? 4 : 0,
            background: bgColor,
            color: textColor,
            backdropFilter: "blur(16px)",
            WebkitBackdropFilter: "blur(16px)",
            border: `1px solid ${borderColor}`,
            boxShadow: isDark ? "0 4px 12px rgba(0, 0, 0, 0.3)" : "0 4px 12px rgba(0, 0, 0, 0.15)",
            maxWidth: maxWidth ? `${maxWidth}px` : undefined,
            width: content && maxWidth ? `${maxWidth}px` : undefined,
          }}
        >
          {icon}
          {content || text}
          <span className={getCaretClasses()} style={getCaretStyle()} />
        </span>
      </span>
    </span>
  );
}
